import React, { useState, useEffect } from 'react';
import { Alert, ConfigService, UserService } from '../../services';
import 'react-loading-skeleton/dist/skeleton.css';
import Pagination from '../../components/Pagination';
import NoDataFound from '../../components/NoDataFound';
import TableComponent from '../../components/TableComponent';
import { formatValue } from '../../utils/Helper';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../../components/StatusBadge';
import SortByFilter from '../../components/listing/SortByFilter';
import SearchInput from '../../components/SearchInput';

const ManageUsers = () => {
  const [userList, setUsersList] = useState([]);
  const [page, setPage] = useState(1);
  const [commonSearch, setCommonSearch] = useState('');
  const [hasInvested, setHasInvested] = useState('');
  const [isLoader, setLoader] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [role] = useState('');
  const [filterData, setFilterData] = useState({
    perPage: 10,
    page: 1,
    commonSearch: '',
    sortBy: '',
    role: '',
    hasInvested: '',
  });
  const [isPagination, setPagination] = useState({
    totalPages: 10,
    currentPage: 1,
  });

  const navigate = useNavigate();

  const tableConfig = {
    columns: {
      name: {
        title: 'Name',
        render: (data) => {
          return (
            <span className="font-medium">
              {data?.full_name ? data.full_name : '--'}
            </span>
          );
        },
      },
      email: {
        title: 'email',
        render: (data) => {
          return data?.email ? data?.email : '--';
        },
      },
      projects: {
        title: 'Projects',
        render: (data) => {
          return data?.totalInvestedProjects
            ? data?.totalInvestedProjects
            : '0';
        },
      },
      invested: {
        title: 'Invested',
        render: (data) => {
          return data?.totalInvestMent
            ? formatValue(data?.totalInvestMent, {
                decimalPlaces: 2,
              })
            : '€0';
        },
      },
      kyc_status: {
        title: 'KYC Status',
        render: (data) => {
          return <StatusBadge status={data?.kyc_status} />;
        },
      },

      joined: {
        title: 'Joined',
        render: (data) => {
          return (
            <span className=" inline-block font-medium">
              {ConfigService.dateFormat(data?.created_at, false)}
            </span>
          );
        },
      },
    },
    data: userList,
  };

  useEffect(() => {
    getUserList();
  }, [filterData]);

  const getUserList = async () => {
    try {
      setLoader(true);

      let response = await UserService.getUserList(
        filterData.perPage,
        filterData.page,
        filterData.commonSearch,
        filterData.sortBy,
        filterData.role,
        filterData.hasInvested,
      );
      if (response.status === 'failure') {
        setLoader(false);
        Alert.error(response.message);
        return false;
      }
      setPagination({
        totalPages: response.data.pagination.totalPages,
        currentPage: response.data.pagination.page,
      });
      setUsersList([...response.data.records]);
      setPage(response.data.pagination.page);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      Alert.error(error.message);
    }
  };

  const searchData = () => {
    const hasInvested = document.getElementById('hasInvested').value;
    const sortBy = document.getElementById('sortBy').value;
    const role = document.getElementById('role').value;
    const commonSearch = document.getElementById('commonSearch').value;

    setFilterData({
      perPage: 10,
      page: page,
      commonSearch: commonSearch,
      sortBy: sortBy,
      role: role,
      hasInvested: hasInvested,
    });
  };

  const handlePagination = (current) => {
    setPagination({ ...isPagination, currentPage: current });
    setFilterData({
      ...filterData,
      page: current,
    });
  };

  const resetFilter = () => {
    setSortBy('');
    setCommonSearch('');
    setHasInvested('');
    setFilterData({
      ...filterData,
      commonSearch: '',
      sortBy: '',
      role: '',
      hasInvested: '',
    });
  };
  // Role options
  const roleOptions = [
    { name: 'Role', value: '' },
    { name: 'Admin', value: 'admin' },
    { name: 'User', value: 'user' },
  ];

  // Investment options
  const investedOptions = [
    { name: 'All Users', value: '' },
    { name: 'Investors Only', value: 'invested' },
    { name: 'Not Invested', value: 'not_invested' },
  ];

  // Sort Options
  const sortOptions = [
    { name: 'Sort By', value: '' },
    { name: 'Old to New', value: 'old_to_new' },
    { name: 'New to Old', value: 'new_to_old' },
  ];

  const userRedirect = (userId) => {
    navigate('/users/view/' + userId);
  };

  return (
    <div className="">
      <h4 className="mb-4">User Management</h4>
      <div className="w-full bg-brandGray px-4 md:px-5 py-4 rounded-xl">
        <div className="lg:grid lg:grid-cols-2 justify-end">
          <div></div>
          <div className="lg:flex justify-end items-center block w-full gap-3">
            <SortByFilter
              value={filterData.role}
              dropdownList={roleOptions}
              filterData={filterData}
              setFilterData={setFilterData}
              setPage={setPage}
              fieldName="role"
            />
            <SortByFilter
              value={filterData.hasInvested}
              dropdownList={investedOptions}
              filterData={filterData}
              setFilterData={setFilterData}
              setPage={setPage}
              fieldName="hasInvested"
            />
            <SortByFilter
              value={filterData.sortBy}
              dropdownList={sortOptions}
              filterData={filterData}
              setFilterData={setFilterData}
              setPage={setPage}
              fieldName="sortBy"
            />
            <div className=" inline-flex relative min-w-full md:min-w-[200px] mb-2 lg:mb-0">
              <SearchInput
                name="commonSearch"
                id="commonSearch"
                onChange={(e) => {
                  setPage(1);
                  searchData();
                  setCommonSearch(e.target.value);
                }}
                value={commonSearch}
                placeholder="Search Keyword"
              />
            </div>
            <p
              className="text-blue-700 rounded bg-blue-500 ml-2 lg:ml-0 bg-opacity-5 text-center font-medium text-sm inline-block cursor-pointer h-[42px] py-3 px-2 min-w-[100px]"
              onClick={() => resetFilter()}
            >
              Clear filters
            </p>
          </div>
        </div>
        <div className="flex flex-col relative overflow-x-auto w-full">
          <TableComponent
            tableConfig={tableConfig}
            isListLoader={isLoader}
            isList={userList}
            redirectTo={userRedirect}
          />

          {userList && userList.length === 0 && isLoader === false && (
            <>
              <NoDataFound />
            </>
          )}
        </div>
        {userList && userList.length > 0 && isLoader === false && (
          <Pagination
            total={isPagination.totalPages}
            current={isPagination.currentPage}
            pagination={(crPage) => handlePagination(crPage)}
          />
        )}
      </div>
    </div>
  );
};
export default ManageUsers;
